import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type { QuoteDetail } from '@techgitter/contracts';
import { promoteQuote } from '../../services/quotesApi';
import { buildQuotePromotionPreview } from '../../utils/quotePromotionPreview';
import {
  buildProductSkuIndex,
  getProductFromIndex,
  useProductCatalog,
} from '../../hooks/useProductCatalog';
import { FIELD_SX } from '../../styles/designSystem';

interface GenerateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
  onPromoted: () => void;
}

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

export const GenerateInvoiceDialog: React.FC<GenerateInvoiceDialogProps> = ({
  open,
  onClose,
  quote,
  onPromoted,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Editable invoice-name override. Default strips the "Quote for "
  // prefix from the quote title because the resulting invoice is
  // already obviously an invoice — keeping the prefix produces titles
  // like "Quote for Acme Pty job 12345" on a brand-new invoice, which
  // reads awkwardly to the customer. Tech can rewrite freely; empty
  // falls back to the raw quote name server-side.
  const defaultInvoiceName = useMemo(
    () => stripQuoteForPrefix(quote.name),
    [quote.name],
  );
  const [invoiceName, setInvoiceName] = useState<string>(defaultInvoiceName);
  // Re-seed the field when the dialog is reopened against a different
  // quote (the parent passes a fresh `quote` prop) or after the quote
  // name has been edited on the detail page since the dialog last opened.
  useEffect(() => {
    setInvoiceName(defaultInvoiceName);
  }, [quote.id, defaultInvoiceName]);

  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';

  // Shared projection — mirrors exactly what the backend bills on promotion
  // (also used by the Generate-ticket dialog).
  // Service lines open with the RS product's catalog description, as the
  // invoice line will (sm4rt-api reads the same catalog).
  const { products } = useProductCatalog();
  const catalogIndex = useMemo(() => buildProductSkuIndex(products), [products]);
  const { lines: previewLines, gstLabel, eftSuffix, lineDisplay, total } =
    useMemo(
      () =>
        buildQuotePromotionPreview(
          quote,
          (sku) => getProductFromIndex(catalogIndex, sku)?.description,
        ),
      [quote, catalogIndex],
    );

  // Editable per-line descriptions, keyed by each line's `lineKey`
  // (present only on customer-facing product/service lines). Seeded from
  // the computed defaults so the tech can fill in placeholders such as
  // "…[Manufacturer][Model]…" before generating. These are transient —
  // sent as `lineDescriptions` on promote and never written back to the
  // quote (same lifetime as the invoice-name override). Re-seeds whenever
  // the dialog targets a different quote / the lines change.
  const [lineDescriptions, setLineDescriptions] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    const seed: Record<string, string> = {};
    for (const l of previewLines) {
      if (l.lineKey) seed[l.lineKey] = l.description;
    }
    setLineDescriptions(seed);
  }, [previewLines]);
  const anyEditable = previewLines.some((l) => l.lineKey);

  const emptyMessage = isServiceShaped
    ? 'This quote has no services with components. Add at least one labour or part line before generating.'
    : 'No items are marked for the invoice. Mark at least one item and pick an option before generating.';

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Collect only the line descriptions the tech actually changed
      // from the computed default — an unchanged (or blank) value is
      // omitted so the backend keeps the computed description.
      const overrides: Record<string, string> = {};
      for (const l of previewLines) {
        if (!l.lineKey) continue;
        const edited = (lineDescriptions[l.lineKey] ?? '').trim();
        if (edited && edited !== l.description.trim()) {
          overrides[l.lineKey] = edited;
        }
      }
      // Pass the (possibly-overridden) invoice name. The API treats an
      // empty/whitespace value as "use the stored quote name", so
      // clearing the field is safe.
      await promoteQuote(quote.number, {
        invoiceName,
        lineDescriptions:
          Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      onPromoted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate invoice from quote</DialogTitle>
      <DialogContent>
        <TextField
          label="Invoice name"
          value={invoiceName}
          onChange={(e) => setInvoiceName(e.target.value)}
          disabled={submitting}
          fullWidth
          size="small"
          helperText="Customer-facing title for the new invoice. Defaults to the quote name; leave blank to keep that default."
          sx={[FIELD_SX, { mt: 1, mb: 2 }]}
        />
        {previewLines.length === 0 ? (
          <Alert severity="warning">{emptyMessage}</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The following line items will be added to a new invoice
              (prices shown {gstLabel}{eftSuffix}).
              {anyEditable
                ? ' Edit any description below before generating — changes'
                  + ' apply to this invoice only and are not saved to the quote.'
                : ''}
            </Typography>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              {previewLines.map((l, idx) => {
                const editable = Boolean(l.lineKey);
                const codeLabel = l.productCode || '(no product code)';
                const helper =
                  l.quantity && l.quantity > 1
                    ? `${codeLabel} · qty ${l.quantity}`
                    : codeLabel;
                return (
                  <ListItem
                    key={l.lineKey ?? idx}
                    divider
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {editable ? (
                        <TextField
                          label="Line description"
                          value={lineDescriptions[l.lineKey as string] ?? ''}
                          onChange={(e) =>
                            setLineDescriptions((prev) => ({
                              ...prev,
                              [l.lineKey as string]: e.target.value,
                            }))
                          }
                          disabled={submitting}
                          fullWidth
                          size="small"
                          multiline
                          helperText={helper}
                          sx={FIELD_SX}
                        />
                      ) : (
                        <ListItemText
                          sx={{ my: 0 }}
                          primaryTypographyProps={{ fontSize: 14, color: '#1a1a1a' }}
                          secondaryTypographyProps={{ fontSize: 12, color: '#888' }}
                          primary={
                            l.quantity && l.quantity > 1
                              ? `${l.description || '(no description)'} (×${l.quantity})`
                              : l.description || '(no description)'
                          }
                          secondary={codeLabel}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'nowrap', pt: editable ? 1 : 0 }}
                    >
                      {fmt(lineDisplay(l))}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                Total ({gstLabel}{eftSuffix}): {fmt(total)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {quote.sm4rtJobId
                ? 'The new invoice will be linked to this quote’s ticket.'
                : 'A standalone invoice will be created for the customer.'}
            </Typography>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || previewLines.length === 0}
        >
          {submitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Generating…
            </>
          ) : (
            'Generate Invoice'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Strip the leading "Quote for " (case-insensitive) from a quote title
 * when seeding the invoice-name field. Mirrors the backend's default
 * naming pattern (see `QuoteService._default_quote_name`) which prefixes
 * customer names with "Quote for". On a promoted invoice the prefix
 * reads awkwardly to the customer — keeping the customer + ticket
 * portion alone is what the tech usually wants. Falls through
 * unchanged when the prefix isn't there (e.g. tech-typed custom names
 * or legacy quote titles).
 */
const stripQuoteForPrefix = (name: string): string => {
  const trimmed = (name || '').trim();
  const match = /^quote\s+for\s+(.+)$/i.exec(trimmed);
  return match ? match[1].trim() : trimmed;
};
